import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { DialogActions, DialogContent } from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';

import { AppButton } from '../app/button/appButton';
import { AppControlledTextField } from '../app/input/appControlledTextField';
import { Client } from '../../models/client';
import { AppControlledAutocomplete } from '../app/input/appControlledAutocomplete';
import { useNavigate } from 'react-router-dom';
import { useGetClients } from '../../hooks/useGetClients';
import { PLEASE_TYPE } from '../../constants';
import { GetClientParams } from '../../services/api/clientServices';
import { AppControlledCheckbox } from '../app/input/appControlledCheckbox';
import { useUserRoles } from '../../hooks/useUserRoles';
import { Employee } from '../../models/employee';
import { useGetEmployeesQuery, useGetEmployeeQuery } from '../../store/employees/employeesService';

interface Props {
  handleClose(): void;
  onSubmit(client: Partial<Client>): void;
  clientEdit?: Client | null;
  isLoading?: boolean;
}

const style = {
  appRow: {
    mt: 3,
  },

  inputLabel: {
    mb: 3,
  },

  textField: {
    mt: 3,
  },

  dialogActions: {
    p: 0,
    mt: 2,
  },

  calendar: {
    width: '170px',
    fontSize: '16px',
  },
} as const;

interface FormData {
  name: string;
  description: string;
  isGlobalDisplay: boolean;
  salesPerson?: Employee;
}

export function ClientForm({
  handleClose,
  onSubmit,
  clientEdit,
  isLoading = false,
}: Props): JSX.Element {
  const defaultValues = {
    name: clientEdit?.name,
    description: clientEdit?.description,
    isGlobalDisplay: clientEdit?.isGlobalDisplay,
  };

  const { isAdmin } = useUserRoles();

  const methods = useForm<FormData>({ defaultValues });
  const isDirty = methods.formState.isDirty;
  const navigate = useNavigate();
  const [filterParams, setFilterParams] = useState<GetClientParams>();
  const [employeesFilter, setEmployeesFilter] = useState<string>('');
  
  const { clients, isLoading: isLoadingExistingClients } = useGetClients(filterParams);
  
  const { data: employeesData, isLoading: isLoadingEmployees } = useGetEmployeesQuery({
    includeInactive: true,
    take: 10,
    skip: 0,
    filter: employeesFilter || undefined,
  });
  
  const { data: initialEmployee } = useGetEmployeeQuery(
    clientEdit?.salesPersonId || '',
    {
      skip: !clientEdit?.salesPersonId,
    }
  );
  
  const employees = useMemo(() => employeesData?.items || [], [employeesData]);

  useEffect(() => {
    if (initialEmployee) {
      methods.setValue('salesPerson', initialEmployee, {
        shouldDirty: false,
      });
    }
  }, [initialEmployee, methods.setValue]);

  const navigeteToClientProjects = useCallback(
    (client: Client) => {
      if (client && client.id) {
        navigate(`/projects/client/${client.id}`, {
          state: { client, projectsComingFromClient: true },
        });
      }
    },
    [navigate]
  );

  const searchExistingClients = useCallback(
    (data) => {
      methods.setValue('name', data, { shouldDirty: true });
      setFilterParams({ filter: data });
    },
    [methods]
  );

  const searchEmployees = useCallback(
    (data: string) => {
      setEmployeesFilter(data);
    },
    []
  );
  
  const handleFormSubmit = useCallback(
    (data: FormData) => {
      const transformedData = {
        ...data,
        salesPersonId: data.salesPerson?.id,
      };
      onSubmit(transformedData);
    },
    [onSubmit]
  );

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={methods.handleSubmit(handleFormSubmit)}
        noValidate
        autoComplete="off"
      >
        <DialogContent>
          <AppControlledAutocomplete
            options={clients}
            getOptionLabel={(client) => typeof client === 'string' ? client : client.name}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            label="Name"
            name="name"
            isRequired
            isError={!!methods.formState.errors.name}
            searchOptions={searchExistingClients}
            isFreeSolo
            autoSelect
            sx={{ mb: 3 }}
            groupBy={() => "Existing clients (click to go to client's page)"}
            isLoading={isLoadingExistingClients}
            onSelect={navigeteToClientProjects}
            debounceDelay={400}
            placeholder={PLEASE_TYPE}
          />
          <AppControlledAutocomplete
            options={employees}
            getOptionLabel={(employee) => typeof employee === 'string' ? employee : employee.name}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            label="Sales"
            name="salesPerson"
            isError={!!methods.formState.errors.salesPerson}
            searchOptions={searchEmployees}
            sx={{ mb: 3 }}
            isLoading={isLoadingEmployees}
            debounceDelay={400}
            placeholder={PLEASE_TYPE}
          />
          <AppControlledTextField
            label="Description"
            name="description"
            isError={!!methods.formState.errors.description}
            isMultiline
            rows={3}
            fullWidth
          />
          {isAdmin && (
            <AppControlledCheckbox
              name="isGlobalDisplay"
              label="Visible for everyone"
              labelSx={{ marginTop: 1 }}
            />
          )}
        </DialogContent>
        <DialogActions sx={style.dialogActions}>
          <AppButton label="Cancel" onClick={handleClose} />
          <AppButton
            label="Submit"
            isPrimary
            buttonType="submit"
            isLoading={isLoading}
            disabled={!isDirty}
          />
        </DialogActions>
      </form>
    </FormProvider>
  );
}
